// URL search params for the Campaigns tab (EPD-12315, EPD-12317, EPD-12318, EPD-5559).

import { CAMPAIGN_STATUSES, type CampaignStatus } from "./status";

/**
 * Defaults (In Progress, Grid) are left out of the URL. Group by isn't in the
 * URL: every visit starts on Brand (EPD-12586).
 */
export interface CampaignsSearch {
  tab?: CampaignStatus;
  view?: "list";
  /** A brand's page within the current tab. */
  brand?: string;
  /** In Progress task filter. */
  tasks?: string[];
  /** The old single task filter; always cleared once it's been read. */
  filterTask?: undefined;
  /** The old Group by param; always cleared. */
  groupBy?: undefined;
}

const isStatus = (value: unknown): value is CampaignStatus =>
  typeof value === "string" &&
  (CAMPAIGN_STATUSES as readonly string[]).includes(value);

const asString = (value: unknown): string | undefined =>
  typeof value === "string" && value.length > 0 ? value : undefined;

/**
 * Parse the URL, mapping the old tabs onto the new ones so bookmarks and older
 * links keep working: "To-do", "By Profile" and "By Brand" become In Progress
 * (grouped by brand, the default), and the old single `filterTask` becomes the
 * task filter.
 */
export function parseCampaignsSearch(
  raw: Record<string, unknown>,
): CampaignsSearch {
  const tab =
    isStatus(raw.tab) && raw.tab !== "in-progress" ? raw.tab : undefined;
  const tasks = Array.isArray(raw.tasks)
    ? raw.tasks.filter((task): task is string => typeof task === "string")
    : asString(raw.filterTask)
      ? [raw.filterTask as string]
      : [];

  // Every key is set, even to undefined: the router layers this over the raw
  // URL params, so a key left out would let an old value (`tab=todo`) through.
  return {
    tab,
    view: raw.view === "list" ? "list" : undefined,
    brand: asString(raw.brand),
    tasks: tasks.length > 0 && !tab ? tasks : undefined,
    filterTask: undefined,
    groupBy: undefined,
  };
}
